import React from 'react';
import { useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { CheckCircle2, Clock, CreditCard } from 'lucide-react';

export default function GuestCheckout() {
  const [searchParams] = useSearchParams();
  const planType = searchParams.get('plan') as 'residential' | 'business' | null;

  // Define all available plans with their Stripe links
  const allPlans = [
    {
      id: 'business',
      title: 'Business',
      price: '£20',
      period: 'per week, per bin',
      description: 'Commercial grade service for businesses',
      features: ['Commercial grade cleaning', 'Multiple bin support', 'Priority scheduling', 'Business documentation'],
      stripeLink: '/pricing' // Uses existing flow
    },
    {
      id: 'grey-blue-monthly',
      title: 'Grey & Blue Bin Only',
      price: '£10',
      period: 'per month',
      description: 'Monthly payment for grey and blue bins',
      features: ['Monthly payment', 'Grey and blue bins only', 'Direct Stripe payment', 'No setup required'],
      stripeLink: 'https://buy.stripe.com/dRmeVcey77QsfBt9APgjC03'
    },
    {
      id: 'annual',
      title: 'Annual Plan',
      price: '£210',
      period: 'per year',
      description: 'Best value - pay annually and save',
      features: ['Full year service', 'Save money annually', 'All bins included', 'Priority support'],
      stripeLink: 'https://buy.stripe.com/8x200i1B3Ac60T00fgjC02'
    },
    {
      id: 'weekly-no-brown',
      title: 'Weekly NO Brown Bin',
      price: '£15',
      period: 'per month',
      description: 'Weekly service for grey, blue and green bins',
      features: ['Weekly service', 'Grey, blue and green bin', 'No brown bin', 'Monthly payment'],
      stripeLink: 'https://buy.stripe.com/aFafZg1LldaM891aETgjC01'
    },
    {
      id: 'weekly-quick',
      title: 'Bin Wash Weekly',
      price: '£5',
      period: 'per week',
      description: 'Quick weekly payment option',
      features: ['Weekly service', 'All bins included', 'Quick setup', 'Direct payment'],
      stripeLink: 'https://buy.stripe.com/28EfZg89J0o0expcN1gjC00',
      popular: true
    }
  ];

  const handlePlanSelect = (plan: any) => {
    if (plan.stripeLink.startsWith('http')) {
      // Direct Stripe payment link
      window.open(plan.stripeLink, '_blank');
    } else {
      // Internal pricing page
      window.location.href = `${plan.stripeLink}?plan=${plan.id}&quick=true`;
    }
  };

  // Show all plans if no specific plan is selected
  if (!planType) {

  const planDetails = {
    residential: {
      title: 'Residential Plan',
      price: '£5',
      period: 'per week',
      description: 'Perfect for homes and small properties'
    },
    business: {
      title: 'Business Plan', 
      price: '£20',
      period: 'per week, per bin',
      description: 'Commercial grade service for businesses'
    }
  };

  const plan = planDetails[planType];

  const handleProceedToPayment = () => {
    // Integrate with existing Stripe checkout
    const checkoutData = {
      planType: planType,
      customerName: '',
      email: '',
      address: '',
      postcode: '',
      binDay: '',
      startDate: new Date().toISOString().split('T')[0],
      isAnnual: false,
      isBusiness: planType === 'business'
    };
    
    // Redirect to pricing page with plan preselected for Stripe integration
    window.location.href = `/pricing?plan=${planType}&quick=true`;
  };

    return (
      <div className="container mx-auto px-4 py-8 max-w-6xl">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold mb-4">Quick Sign Up</h1>
          <p className="text-muted-foreground">
            Choose your plan and start your bin washing service immediately with secure Stripe payment.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {allPlans.map((plan) => (
            <Card key={plan.id} className={`relative ${plan.popular ? 'border-blue-500 shadow-lg' : 'hover:shadow-md transition-shadow'}`}>
              {plan.popular && (
                <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 bg-blue-500 text-white px-3 py-1 rounded-full text-xs font-medium">
                  Most Popular
                </div>
              )}
              <CardHeader className="text-center pb-4">
                <CardTitle className="text-lg">{plan.title}</CardTitle>
                <div className="mt-2">
                  <span className="text-2xl font-bold text-blue-600">{plan.price}</span>
                  <span className="text-sm text-muted-foreground ml-1">{plan.period}</span>
                </div>
                <p className="text-xs text-muted-foreground mt-1">{plan.description}</p>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2 mb-6">
                  {plan.features.map((feature: string, index: number) => (
                    <li key={index} className="flex items-start text-sm">
                      <CheckCircle2 className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
                <Button 
                  className="w-full" 
                  variant={plan.popular ? "default" : "outline"}
                  onClick={() => handlePlanSelect(plan)}
                >
                  <CreditCard className="w-4 h-4 mr-2" />
                  {plan.stripeLink.startsWith('http') ? 'Pay with Stripe' : 'Select Plan'}
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>

        <div className="mt-12 text-center">
          <div className="bg-green-50 rounded-lg p-6 mb-6">
            <h3 className="font-semibold mb-4">Why Choose Quick Sign Up?</h3>
            <div className="grid md:grid-cols-3 gap-4 text-sm">
              <div className="flex items-center justify-center">
                <Clock className="w-4 h-4 text-green-600 mr-2" />
                <span>Start service immediately</span>
              </div>
              <div className="flex items-center justify-center">
                <CreditCard className="w-4 h-4 text-green-600 mr-2" />
                <span>Secure Stripe payments</span>
              </div>
              <div className="flex items-center justify-center">
                <CheckCircle2 className="w-4 h-4 text-green-600 mr-2" />
                <span>No account required</span>
              </div>
            </div>
          </div>

          <p className="text-sm text-muted-foreground mb-4">
            Admin users can access the management dashboard here:
          </p>
          <Button variant="outline" asChild>
            <a href="/admin">
              Admin Access
            </a>
          </Button>
        </div>
      </div>
    );
  }

  const planDetails = {
    residential: {
      title: 'Residential Plan',
      price: '£5',
      period: 'per week',
      description: 'Perfect for homes and small properties'
    },
    business: {
      title: 'Business Plan', 
      price: '£20',
      period: 'per week, per bin',
      description: 'Commercial grade service for businesses'
    }
  };

  const plan = planDetails[planType];

  const handleProceedToPayment = () => {
    // Integrate with existing Stripe checkout
    const checkoutData = {
      planType: planType,
      customerName: '',
      email: '',
      address: '',
      postcode: '',
      binDay: '',
      startDate: new Date().toISOString().split('T')[0],
      isAnnual: false,
      isBusiness: planType === 'business'
    };
    
    // Redirect to pricing page with plan preselected for Stripe integration
    window.location.href = `/pricing?plan=${planType}&quick=true`;
  };

  return (
    <div className="container mx-auto px-4 py-8 max-w-3xl">
      <div className="text-center mb-8">
        <h1 className="text-3xl font-bold mb-4">Guest Checkout</h1>
        <p className="text-muted-foreground">
          Start your bin washing service immediately. You can create an account later to manage your subscription.
        </p>
      </div>

      <div className="grid md:grid-cols-2 gap-8">
        {/* Plan Summary */}
        <Card>
          <CardHeader>
            <CardTitle>Selected Plan</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div>
                <h3 className="text-xl font-semibold">{plan.title}</h3>
                <p className="text-2xl font-bold text-blue-600">
                  {plan.price} <span className="text-sm font-normal">{plan.period}</span>
                </p>
                <p className="text-sm text-muted-foreground">{plan.description}</p>
              </div>
              
              <div className="space-y-2">
                <div className="flex items-center text-sm">
                  <CheckCircle2 className="w-4 h-4 text-green-500 mr-2" />
                  SEPA-compliant service
                </div>
                <div className="flex items-center text-sm">
                  <CheckCircle2 className="w-4 h-4 text-green-500 mr-2" />
                  Eco-friendly cleaning
                </div>
                <div className="flex items-center text-sm">
                  <CheckCircle2 className="w-4 h-4 text-green-500 mr-2" />
                  No contract required
                </div>
                <div className="flex items-center text-sm">
                  <CheckCircle2 className="w-4 h-4 text-green-500 mr-2" />
                  Cancel anytime
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Guest Checkout Form */}
        <Card>
          <CardHeader>
            <CardTitle>Quick Start</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <Alert>
              <Clock className="w-4 h-4" />
              <AlertDescription>
                We'll collect your details during the secure payment process. No account required to start!
              </AlertDescription>
            </Alert>

            <div className="space-y-4">
              <h4 className="font-semibold">What happens next:</h4>
              <ol className="space-y-2 text-sm">
                <li className="flex items-start">
                  <span className="bg-blue-500 text-white w-5 h-5 rounded-full flex items-center justify-center text-xs mr-3 mt-0.5">1</span>
                  Secure payment processing with Stripe
                </li>
                <li className="flex items-start">
                  <span className="bg-blue-500 text-white w-5 h-5 rounded-full flex items-center justify-center text-xs mr-3 mt-0.5">2</span>
                  We'll contact you to schedule your first clean
                </li>
                <li className="flex items-start">
                  <span className="bg-blue-500 text-white w-5 h-5 rounded-full flex items-center justify-center text-xs mr-3 mt-0.5">3</span>
                  Automated service begins immediately
                </li>
                <li className="flex items-start">
                  <span className="bg-blue-500 text-white w-5 h-5 rounded-full flex items-center justify-center text-xs mr-3 mt-0.5">4</span>
                  Optional: Create account for easy management
                </li>
              </ol>
            </div>

            <Button 
              className="w-full h-12 text-base"
              onClick={handleProceedToPayment}
            >
              <CreditCard className="w-5 h-5 mr-2" />
              Proceed to Secure Payment
            </Button>

            <p className="text-xs text-center text-muted-foreground">
              Powered by Stripe • Secure & encrypted • No hidden fees
            </p>
          </CardContent>
        </Card>
      </div>

      <div className="mt-8 text-center">
        <p className="text-sm text-muted-foreground mb-4">
          Admin users can access the management dashboard here:
        </p>
        <Button variant="outline" asChild>
          <a href={`/admin?plan=${planType}&action=signup`}>
            Admin Access
          </a>
        </Button>
      </div>
    </div>
  );
}